import type { FC } from "react";
import React from "react";
import { VirtuosoGrid } from "react-virtuoso";

import { getUserPresentableMessage } from "@core/foundation/error";

import { EmptyView, ErrorView } from "@react/foundation/placeholder";
import { Skeleton } from "@react/ui-primitives/skeleton";
import { Spinner } from "@react/ui-primitives/spinner";

import type { MediaAsset } from "@apis/media/models";

import { MediaThumb } from "./MediaThumb.tsx";

interface MediaListItem {
  readonly mediaAsset: MediaAsset;
  readonly previewUrl: string;
}

interface MediaListProps {
  readonly error: Error | null;
  readonly isFetchingNextPage: boolean;
  readonly isPending: boolean;
  readonly items: readonly MediaListItem[];
  readonly onEndReached: () => void;
  readonly skeletonCount?: number;
}

export const MediaList: FC<MediaListProps> = ({
  error,
  isFetchingNextPage,
  isPending,
  items,
  onEndReached,
  skeletonCount = 12,
}) => {
  if (isPending) {
    return (
      <div
        className={`
          grid grid-cols-2 gap-3
          sm:grid-cols-3
          lg:grid-cols-4
        `}
      >
        {Array.from({ length: skeletonCount }, (_, index) => (
          <Skeleton className="aspect-square" key={index} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <ErrorView
        description={getUserPresentableMessage(error)}
        title="Failed to load media"
      />
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex min-h-64">
        <EmptyView
          description="Uploaded media will appear here."
          title="No media yet"
        />
      </div>
    );
  }

  return (
    <VirtuosoGrid
      components={{
        Footer: () =>
          isFetchingNextPage ? (
            <div className="flex justify-center py-3">
              <Spinner />
            </div>
          ) : null,
      }}
      computeItemKey={(_index, item) => item.mediaAsset.id}
      data={items}
      endReached={onEndReached}
      itemContent={(_index, item) => (
        <MediaThumb asset={item.mediaAsset} previewUrl={item.previewUrl} />
      )}
      listClassName={`
        grid grid-cols-2 gap-3
        sm:grid-cols-3
        lg:grid-cols-4
      `}
      useWindowScroll
    />
  );
};
